import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  Box,
  Typography,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  Chip,
  CircularProgress,
  TextField,
} from "@mui/material";
import { Handle, Position, useStore } from "@xyflow/react";
import { callAPI } from "../../../../functions/api";
import { NodeControls } from "./nodeControls";
import { set } from "lodash";
import { useTheme } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CancelIcon from "@mui/icons-material/Cancel";
const DefaultNode = ({ id, data, selected }) => {
  //styling
  const theme = useTheme();
  const isLight = theme.palette.mode === "light";
  const borderColor = selected ? "#27BBD9" : isLight ? "#bbb" : "#C3C9D5";
  const backgroundColor = isLight ? "#f9f9f9" : "#414243";
  const textColor = isLight ? "#000" : "#fff";
  const subTextColor = isLight ? "#666" : "gray";
  const inputHandleColor = isLight ? theme.palette.primary.main : "#3182ce";
  const outputHandleColor = isLight ? "#00BFA5" : "#00C49F";

  // Check if this is an Excel export node
  const isExcelExport = data.type === "data.exporttoexcel";
  // get the nodes and edges from the store
  const nodes = useStore((store) => store.nodes);
  const edges = useStore((store) => store.edges);
  const xPos = useStore((store) => store.nodes.find((n) => n.id === id)?.position?.x);
  const yPos = useStore((store) => store.nodes.find((n) => n.id === id)?.position?.y);
  const [dynamicOptions, setDynamicOptions] = useState({});
  const [dynamicData, setDynamicData] = useState({});
  const [node, setNode] = useState(null); // display node data in console for debugging  // Log node details for debugging
  useEffect(() => {
    if (isExcelExport) {
      console.log("Excel Export Node data for", id, ":", {
        type: data.type,
        model: data.model,
        hasInputs: data.model?.inputs?.length > 0,
        hasOutputs: data.model?.outputs?.length > 0,
        inputs: data.model?.inputs,
        outputs: data.model?.outputs,
      });
    }
  }, [id, data, isExcelExport]);

  // useEffect to handle changes in nodes and edges
  useEffect(() => {
    const currentNode = nodes.find((n) => n.id === id);
    if (currentNode) {
      setNode(currentNode);
    } else {
      setNode(null);
    }
  }, [nodes]);
  useEffect(() => {}, [data.model]);
  // fetch dynamic options if needed with debounce to prevent API calls during drag
  const fetchOptionsTimerRef = useRef(null);
  const previousSettingsRef = useRef(null);
  const isDraggingRef = useRef(false);

  // Track position changes to detect dragging
  useEffect(() => {
    const positionChanged = xPos !== undefined && yPos !== undefined;
    if (positionChanged) {
      isDraggingRef.current = true;
      // Clear any existing timer when dragging starts/continues
      if (fetchOptionsTimerRef.current) {
        clearTimeout(fetchOptionsTimerRef.current);
      }

      // Set a timeout to reset dragging state after dragging stops
      const dragTimeout = setTimeout(() => {
        isDraggingRef.current = false;
      }, 500);

      return () => clearTimeout(dragTimeout);
    }
  }, [xPos, yPos]);

  useEffect(() => {
    // Skip API calls during dragging
    if (isDraggingRef.current) {
      return;
    }

    // Check if settings configuration actually changed
    const currentSettings = JSON.stringify(data.settings || {});
    if (currentSettings === previousSettingsRef.current) {
      return; // Skip if nothing changed
    }
    previousSettingsRef.current = currentSettings;

    // Clear any existing timer
    if (fetchOptionsTimerRef.current) {
      clearTimeout(fetchOptionsTimerRef.current);
    }

    // Set a new timer to debounce API calls
    fetchOptionsTimerRef.current = setTimeout(async () => {
      if (data.settings && Object.keys(data.settings).length > 0) {
        const newOptions = {};

        for (const [settingKey, setting] of Object.entries(data.settings)) {
          if (setting.type === "dropdown") {
            let options = [];

            if (setting.api?.endPoint) {
              console.log("----------------------- WTF");
              const response = await callAPI(setting.api.endPoint, "GET", null, true);
              const apiResult = response || {};

              setDynamicData((prev) => ({
                ...prev,
                [settingKey]: apiResult,
              }));

              if (setting.dataType === "object") {
                options = Object.keys(apiResult).map((key) => ({
                  [setting.keyField]: key,
                  [setting.displayField]: key,
                }));
              }

              if (setting.dataType === "array") {
                options = apiResult.map((item) => ({
                  [setting.keyField]: item[setting.keyField],
                  [setting.displayField]: item[setting.displayField],
                }));
              }
            } else {
              options = setting.options || [];
            }

            newOptions[settingKey] = options;
          }
        }

        setDynamicOptions(newOptions);
      }
    }, 500); // Wait 500ms after changes before making API calls

    return () => {
      if (fetchOptionsTimerRef.current) {
        clearTimeout(fetchOptionsTimerRef.current);
      }
    };
  }, [data.id, data.settings]);

  // decide if we shoudl show setting button
  // useEffect(() => {
  //   const requiresInput = data.model?.settingsRequireInput === true;
  //   const hasSettings = data.model?.settings && Object.keys(data.model.settings).length > 0;

  //   if (!requiresInput) {
  //     console.log("data---", data);
  //     if (data.model.customSettings) {
  //       setShowSettings(true);
  //       return;
  //     }

  //     setShowSettings(hasSettings);
  //     return;
  //   }

  //   // If settingsRequireInput === true, check connected nodes
  //   const connectedEdges = edges.filter((e) => e.target === id);
  //   const connectedNodes = connectedEdges
  //     .map((edge) => nodes.find((node) => node.id === edge.source))
  //     .filter(Boolean);

  //   // No connected nodes → don't show settings
  //   if (connectedNodes.length === 0) {
  //     setShowSettings(false);
  //     return;
  //   }

  //   let hasAnySelectedValue = false;
  //   let hasMissingRequired = false;

  //   for (const node of connectedNodes) {
  //     const settings = node.data.model?.settings || [];
  //     const settings = node.data.settings || {};

  //     for (const s of settings) {
  //       if (s.required && !s.selectedValue) hasMissingRequired = true;
  //       if (s.selectedValue) hasAnySelectedValue = true;
  //     }

  //     // Check node settings instead of inline
  //     const nodeSettings = node.data.settings || {};
  //     for (const [key, setting] of Object.entries(nodeSettings)) {
  //       if (setting.required && !setting.selectedValue) hasMissingRequired = true;
  //       if (setting.selectedValue) hasAnySelectedValue = true;
  //     }
  //   }

  //   if (!hasMissingRequired && hasAnySelectedValue) {
  //     setShowSettings(true);
  //   } else {
  //     setShowSettings(false);
  //   }
  // }, [JSON.stringify(nodes), JSON.stringify(edges), id, data.model]);

  useEffect(() => {
    const settings = data.model?.settings || {};
    const nodeSettings = data.settings || {};

    const settingsValues = Object.entries(settings).reduce((acc, [key, setting]) => {
      if (setting.selectedValue !== undefined) {
        acc[key] = setting.selectedValue;
      }
      return acc;
    }, {});

    const nodeSettingsValues = Object.entries(nodeSettings).reduce((acc, [key, setting]) => {
      if (setting !== undefined) {
        acc[key] = setting;
      }
      return acc;
    }, {});

    const updatedData = {
      ...settingsValues,
      ...nodeSettingsValues,
    };

    if (data.setData) {
      data.setData((oldData) => ({
        ...oldData,
        model: {
          ...oldData.model,
          data: updatedData,
        },
      }));
    }
  }, [data.id]); // Only re-run when the node ID changes

  const handleSettingsChange = (settingKey, value) => {
    const fullApiData = dynamicData[settingKey];
    const selectedResult = fullApiData?.[value] || null;

    if (data.setData) {
      data.setData((oldData) => {
        const newSettings = {
          ...oldData.settings,
          [settingKey]: value,
        };

        return {
          ...oldData,
          settings: newSettings,
          model: {
            ...oldData.model,
            apiResult: {
              ...(oldData.model.apiResult || {}),
              [settingKey]: selectedResult,
            },
          },
        };
      });
    }

    if (data.onSettingsChange) {
      data.onSettingsChange(settingKey, value);
    }
  };

  const renderSettingsControls = useMemo(() => {
    if (!data.settings || Object.keys(data.settings).length === 0) {
      return null;
    }

    return Object.entries(data.settings).map(([settingKey, setting], idx) => {
      let control;

      switch (setting.type) {
        case "dropdown":
          const options = dynamicOptions[settingKey] || setting.options || [];

          control = (
            <Box sx={{ marginTop: 0, display: "flex", flexDirection: "column", flex: 1 }}>
              <FormControl fullWidth size="small">
                <div onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                  <Select
                    sx={{
                      width: "100%",
                      color: textColor,
                      backgroundColor: isLight ? "#fff" : "#333333",
                    }}
                    native
                    value={setting.selectedValue || setting.value || ""}
                    label={setting.label || "Select an option"}
                    onChange={(e) => handleSettingsChange(settingKey, e.target.value)}
                  >
                    <option value="">-- select --</option>
                    {options.map((option) => (
                      <option
                        key={option[setting.keyField] || option.value || option}
                        value={option[setting.keyField] || option.value || option}
                      >
                        {option[setting.displayField] || option.label || option.value || option}
                      </option>
                    ))}
                  </Select>
                </div>
              </FormControl>
            </Box>
          );
          break;

        case "text":
        case "string":
          control = (
            <Box sx={{ marginTop: 0, display: "flex", flexDirection: "column", flex: 1 }}>
              <TextField
                size="small"
                value={setting.value || ""}
                label={setting.label || "Enter value"}
                onChange={(e) => handleSettingsChange(settingKey, e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                sx={{
                  "& .MuiInputBase-root": {
                    color: textColor,
                    backgroundColor: isLight ? "#fff" : "#333333",
                  },
                }}
              />
            </Box>
          );
          break;

        default:
          // For any setting that doesn't have a specific type, treat it as a simple value display
          if (setting !== undefined && setting !== null && setting !== "") {
            control = (
              <Box sx={{ marginTop: 0, padding: 1 }}>
                <Typography variant="caption" sx={{ color: textColor }}>
                  {settingKey}: {String(setting)}
                </Typography>
              </Box>
            );
          } else {
            control = (
              <Typography>Unknown control type: {setting.type || typeof setting}</Typography>
            );
          }
      }

      return (
        <Box key={idx} sx={{ mb: 1 }}>
          {control}
        </Box>
      );
    });
  }, [data.settings, dynamicOptions, textColor, isLight, handleSettingsChange]);

  const renderNodeMessages = () => {
    const hasSettingsControls = data.settings && Object.keys(data.settings).length > 0;
    const requiresSettings = data.requiresSettings === true;
    const messages = [];

    // Check for RequiresSettings warning first
    if (requiresSettings) {
      // Check if the node has any configured settings
      let hasConfiguredSettings = false;

      // Check model settings
      if (data.model.settings && Object.keys(data.model.settings).length > 0) {
        hasConfiguredSettings = Object.values(data.model.settings).some(
          (setting) =>
            setting.selectedValue !== undefined &&
            setting.selectedValue !== "" &&
            setting.selectedValue !== null
        );
      }

      // Check node settings
      if (!hasConfiguredSettings && data.settings && Object.keys(data.settings).length > 0) {
        hasConfiguredSettings = Object.values(data.settings).some(
          (setting) =>
            setting.selectedValue !== undefined &&
            setting.selectedValue !== "" &&
            setting.selectedValue !== null
        );
      }

      // Check output settings as well
      if (!hasConfiguredSettings && data.output?.settings) {
        const outputSettings = data.output.settings;
        hasConfiguredSettings = Object.values(outputSettings).some(
          (setting) =>
            setting.selectedValue !== undefined &&
            setting.selectedValue !== "" &&
            setting.selectedValue !== null
        );
      }

      // Check for selectedModel and selectedSchema in output (used by model nodes)
      if (!hasConfiguredSettings && data.output) {
        hasConfiguredSettings =
          (data.output.selectedModel && data.output.selectedModel !== "") ||
          (data.output.selectedSchema && data.output.selectedSchema !== "");
      }

      // Check model data for any configured values
      if (!hasConfiguredSettings && data.model.data) {
        hasConfiguredSettings = Object.values(data.model.data).some(
          (value) => value !== undefined && value !== "" && value !== null
        );
      }

      if (!hasConfiguredSettings) {
        messages.push({
          color: "orange",
          message: "⚠ Settings required but not configured",
        });
      }
    }

    if (hasSettingsControls) {
      const hasConfiguredNodeSettings = Object.values(data.settings).some(
        (setting) =>
          setting.selectedValue !== undefined &&
          setting.selectedValue !== "" &&
          setting.selectedValue !== null
      );

      if (hasConfiguredNodeSettings) {
        messages.push({
          color: "lightgreen",
          message: "✔ Settings configured",
        });
      } else {
        messages.push({
          color: "orange",
          message: "⚠ Settings not configured",
        });
      }
    }

    return (
      <Box sx={{ mt: 0 }}>
        {messages.map((msg, idx) => (
          <Typography key={idx} variant="subtitle2" sx={{ fontSize: 11, mt: 0, color: msg.color }}>
            {msg.message}
          </Typography>
        ))}
      </Box>
    );
  };

  const renderConnectedNodeInfo = useMemo(() => {
    const connectedEdges = edges.filter((e) => e.target === id);
    const connectedNodes = connectedEdges
      .map((edge) => nodes.find((n) => n.id === edge.source))
      .filter(Boolean);

    const messages = [];

    if ((data.model.inputs || []).length === 0) return null;

    if (connectedNodes.length === 0) {
      messages.push({
        color: "orange",
        message: "⚠ No connected nodes found",
      });
    } else {
      connectedNodes.forEach((sourceNode) => {
        const label = sourceNode.data?.label || sourceNode.type || "Unnamed Node";

        messages.push({
          color: "lightgreen",
          message: `✔ Connected to ${label}`,
        });

        const nodeSettings = sourceNode.data?.settings || {};
        const hasConfiguredSettings = Object.values(nodeSettings).some(
          (setting) =>
            setting.selectedValue !== undefined &&
            setting.selectedValue !== "" &&
            setting.selectedValue !== null
        );

        if (hasConfiguredSettings) {
          messages.push({
            color: "lightgreen",
            message: `✔ Input: ${label} (configured)`,
          });
        } else if (Object.keys(nodeSettings).length > 0) {
          messages.push({
            color: "orange",
            message: `⚠ Input node not configured: ${label}`,
          });
        }
      });
    }

    return (
      <Box sx={{ mt: 0 }}>
        {messages.map((msg, idx) => (
          <Typography
            key={`${id}-msg-${idx}`}
            variant="subtitle2"
            sx={{ fontSize: 11, mt: 0, color: msg.color }}
          >
            {msg.message}
          </Typography>
        ))}
      </Box>
    );
  }, [id, nodes, edges]);
  // Get status-based styling
  const getStatusStyling = () => {
    const baseColor = selected ? "#27BBD9" : isLight ? "#bbb" : "#C3C9D5";

    // Use status from data prop instead of node from store to ensure real-time updates
    const currentStatus = data.status || node?.status;

    switch (currentStatus) {
      case "running":
        return {
          borderColor: theme.palette.warning.main, // Orange for running
          glowColor: `${theme.palette.warning.main}30`, // 30 = 18.8% opacity
          statusColor: theme.palette.warning.main,
          statusLabel: "Running",
        };
      case "pending":
        return {
          borderColor: theme.palette.info.main, // Blue for pending
          glowColor: `${theme.palette.info.main}30`,
          statusColor: theme.palette.info.main,
          statusLabel: "Pending",
        };
      case "completed":
        return {
          borderColor: theme.palette.success.main, // Green for success
          glowColor: `${theme.palette.success.main}30`,
          statusColor: theme.palette.success.main,
          statusLabel: "Completed",
        };
      case "error":
        return {
          borderColor: theme.palette.error.main, // Red for error
          glowColor: `${theme.palette.error.main}30`,
          statusColor: theme.palette.error.main,
          statusLabel: "Error",
        };
      case "cancelled":
        return {
          borderColor: theme.palette.grey[500], // Grey for cancelled
          glowColor: `${theme.palette.grey[500]}30`,
          statusColor: theme.palette.grey[500],
          statusLabel: "Cancelled",
        };
      default:
        return {
          borderColor: baseColor,
          glowColor: "transparent",
          statusColor: null,
          statusLabel: null,
        };
    }
  };
  const statusStyling = getStatusStyling();
  // Render status badge/icon
  const renderStatusBadge = () => {
    // Use status from data prop instead of node from store to ensure real-time updates
    const currentStatus = data.status || node?.status;
    if (!currentStatus || currentStatus === "idle") return null;

    const getStatusIcon = () => {
      switch (currentStatus) {
        case "running":
          return <CircularProgress size={14} sx={{ color: statusStyling.statusColor }} />;
        case "pending":
          return <PlayArrowIcon sx={{ fontSize: 16, color: statusStyling.statusColor }} />;
        case "completed":
          return <CheckCircleIcon sx={{ fontSize: 16, color: statusStyling.statusColor }} />;
        case "error":
          return <ErrorIcon sx={{ fontSize: 16, color: statusStyling.statusColor }} />;
        case "cancelled":
          return <CancelIcon sx={{ fontSize: 16, color: statusStyling.statusColor }} />;
        default:
          return null;
      }
    };

    return (
      <Box
        sx={{
          position: "absolute",
          top: -8,
          right: -8,
          backgroundColor: backgroundColor,
          borderRadius: "50%",
          border: `2px solid ${statusStyling.statusColor}`,
          width: 24,
          height: 24,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 10,
          boxShadow: `0 2px 8px ${statusStyling.glowColor}`,
          transition: "all 0.2s ease-in-out",
        }}
        role="status"
        aria-label={statusStyling.statusLabel}
        title={statusStyling.statusLabel}
      >
        {getStatusIcon()}
      </Box>
    );
  };

  // Validation logic to determine if node can execute
  const canExecute = useMemo(() => {
    if (!node) return false;

    const nodeData = node.data;
    const requiredInputs = nodeData?.model?.inputs || [];

    // Check if node requires inputs but has none connected
    const inputEdges = edges.filter((edge) => edge.target === id);
    const hasRequiredInputs = requiredInputs.length === 0 || inputEdges.length > 0;

    // Check if node has required settings and they are configured
    const settings = nodeData?.model?.settings || {};
    const requiredSettings = Object.values(settings).filter((s) => s.required);
    const hasRequiredSettings =
      requiredSettings.length === 0 ||
      requiredSettings.every((s) => s.selectedValue !== undefined && s.selectedValue !== "");

    // Check node-specific output settings if they exist
    const outputSettings = nodeData?.output?.settings || {};
    let hasOutputConfiguration = true;

    // For nodes with fieldsToInclude, check if at least one field is selected
    if (outputSettings.hasOwnProperty("fieldsToInclude")) {
      hasOutputConfiguration = (outputSettings.fieldsToInclude || []).length > 0;
    }

    // For nodes with selectedModel, check if model is selected
    if (outputSettings.hasOwnProperty("selectedModel")) {
      hasOutputConfiguration = !!outputSettings.selectedModel;
    }
    // console.log("DefaultNode validation:", {
    //   nodeId: id,
    //   nodeName: nodeData?.name,
    //   requiredInputs: requiredInputs.length,
    //   connectedInputs: inputEdges.length,
    //   hasRequiredInputs,
    //   hasRequiredSettings,
    //   hasOutputConfiguration,
    //   canExecute: hasRequiredInputs && hasRequiredSettings && hasOutputConfiguration
    // });

    return hasRequiredInputs && hasRequiredSettings && hasOutputConfiguration;
  }, [node, edges, id]);

  return (
    <Box
      sx={{
        backgroundColor,
        color: textColor,
        paddingLeft: 2,
        borderTopLeftRadius: data.name == "trigger" ? "20px" : "8px",
        borderBottomLeftRadius: data.name == "trigger" ? "20px" : "8px",
        borderTopRightRadius: "8px",
        borderBottomRightRadius: "8px",
        paddingRight: 3,
        paddingTop: 1.5,
        paddingBottom: 1.5,
        maxWidth: 280,
        border: `2px solid ${statusStyling.borderColor}`,
        overflow: "visible",
        position: "relative",
        zIndex: 2,
        transition: "border 0.2s",
        "&:hover .node-controls": { opacity: 1, pointerEvents: "auto" },
        boxShadow:
          statusStyling.glowColor !== "transparent"
            ? `0 0 12px ${statusStyling.glowColor}`
            : "none",
      }}
    >
      {/* Status Badge */}
      {renderStatusBadge()}{" "}
      {(node?.status != "running" || data.model?.execute) && (
        <NodeControls
          nodeId={id}
          nodeStatus={node?.status}
          onSettingsClick={() => {
            try {
              console.log("Opening settings for node:", id, "Type:", data.type);

              // Special handling for Excel export nodes - we need to make sure the component is properly registered
              if (data.type === "data.exporttoexcel") {
                console.log("Excel export node settings clicked. Special handling applied.");
                // Use setTimeout to ensure we don't block the UI
                setTimeout(() => {
                  if (typeof data.onSettingsClick === "function") {
                    data.onSettingsClick(id);
                  }
                }, 0);
              } else {
                // Normal flow for other node types
                if (typeof data.onSettingsClick === "function") {
                  data.onSettingsClick(id);
                } else {
                  console.error("onSettingsClick is not a function for node:", id);
                }
              }
            } catch (error) {
              console.error("Error opening settings for node:", id, error);
            }
          }}
          {...(data.model?.execute && canExecute ? { onRun: () => data.onRun?.(id) } : {})}
          {...(data.model?.disable ? { onDisable: () => data.onDisable(id) } : {})}
        />
      )}{" "}
      {/* Input handles - show at least one input handle for non-trigger nodes */}
      {(() => {
        const inputs = data.model?.inputs || [];
        // Excel export nodes always need an input handle
        const shouldShowInputs =
          inputs.length > 0 || !data.type?.startsWith("trigger.") || isExcelExport;
        // For Excel export nodes, always use "input" handle ID for consistency
        const handlesToRender = isExcelExport ? ["input"] : inputs.length > 0 ? inputs : ["input"];

        return (
          shouldShowInputs &&
          handlesToRender.map((input, index) => {
            const handleId = typeof input === "string" ? input : `input-${index}`;
            return (
              <Handle
                type="target"
                key={handleId}
                position={Position.Left}
                id={handleId}
                style={{
                  backgroundColor: inputHandleColor,
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  left: -9,
                  top: `${50 + index * 20}%`,
                  transform: "translateY(-50%)",
                  border: "2px solid white",
                }}
              />
            );
          })
        );
      })()}
      {/* Description and Logo Box */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0, mb: 0 }}>
        <Box sx={{ display: "flex", flexDirection: "row", alignItems: "self-start", gap: 1 }}>
          {/* render icons */}
          {data.icon && data.icon.length > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                mt: 0.1,
              }}
            >
              <img
                src={require(`../../../../assets/${data.icon}.png`)}
                alt="Node Icon"
                style={{ height: "20px" }}
              />
            </Box>
          )}

          {/* Node Title and Description */}
          {data.label && data.label.length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 1 }}>
              <Typography fontWeight="bold">{data.label || "Default Node"}</Typography>
              {/* Execution Output Indicator */}
              {data.execution?.success && data.execution?.output && (
                <Box
                  sx={{
                    backgroundColor: "#4caf50",
                    borderRadius: "50%",
                    width: 8,
                    height: 8,
                    flexShrink: 0,
                  }}
                  title="Node has execution output data"
                />
              )}
            </Box>
          )}
        </Box>
        {data.description && data.description.length > 0 && (
          <Box sx={{ mb: 1, mt: 0.5 }}>
            <Typography fontSize={12} color={subTextColor}>
              {data.description || ""}
            </Typography>
          </Box>
        )}
        {/* Status Display Section */}
        {node?.status != "running" ? (
          <>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "flex-start",
                margin: 0,
                padding: 0,
              }}
            >
              {/* settings controls */}
              {renderSettingsControls}
            </Box>
            <Box
              sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0 }}
            >
              {/* Node Messages */}
              {renderNodeMessages()}

              {/* connected node info */}
              {renderConnectedNodeInfo}
            </Box>
          </>
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "flex-start",
                margin: 0,
                padding: 0,
              }}
            >
              {/* settings controls */}
              {renderSettingsControls}
            </Box>
            <Box
              sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0 }}
            >
              {/* Node Messages */}
              {renderNodeMessages()}

              {/* connected node info */}
              {renderConnectedNodeInfo}
            </Box>
          </>
        )}

        {node?.status != "idle" && (
          <Box sx={{ mt: 1, height: 22, position: "relative" }}>
            {/* Running status */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                display: "flex",
                alignItems: "center",
                gap: 1,
                opacity: node?.status === "running" ? 1 : 0,
                transform: node?.status === "running" ? "translateY(0)" : "translateY(-3px)",
                transition: "opacity 0.3s ease, transform 0.3s ease",
                visibility: node?.status === "running" ? "visible" : "hidden",
              }}
            >
              <CircularProgress size={14} sx={{ color: "#FF6B35" }} />
              <Typography fontSize={11} sx={{ color: "#FF6B35", fontWeight: "medium" }}>
                {data.statusMessage || "Running..."}
              </Typography>
            </Box>

            {/* Completed status */}
            <Box
              sx={{
                position: "absolute",
                top: "-5px",
                left: 0,
                display: "flex",
                alignItems: "center",
                gap: 0,
                opacity: node?.status === "completed" ? 1 : 0,
                transform: node?.status === "completed" ? "translateY(0)" : "translateY(-3px)",
                transition: "opacity 0.3s ease, transform 0.3s ease",
                visibility: node?.status === "completed" ? "visible" : "hidden",
              }}
            >
              <CheckCircleIcon
                sx={{
                  fontSize: 14,
                  color: "#4CAF50",
                  top: "-3px",
                  left: "-2px",
                  position: "relative",
                }}
              />
              <Typography fontSize={11} sx={{ color: "#4CAF50", fontWeight: "medium" }}>
                {data.statusMessage || "Completed successfully"}
              </Typography>
            </Box>

            {/* Error status */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                display: "flex",
                alignItems: "center",
                gap: 1,
                opacity: node?.status === "error" ? 1 : 0,
                transform: node?.status === "error" ? "translateY(0)" : "translateY(-3px)",
                transition: "opacity 0.3s ease, transform 0.3s ease",
                visibility: node?.status === "error" ? "visible" : "hidden",
              }}
            >
              <ErrorIcon sx={{ fontSize: 14, color: "#F44336" }} />
              <Typography fontSize={11} sx={{ color: "#F44336", fontWeight: "medium" }}>
                {data.statusMessage || "Error occurred"}
              </Typography>
            </Box>

            {/* Pending status */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                display: "flex",
                alignItems: "center",
                gap: 1,
                opacity: node?.status === "pending" ? 1 : 0,
                transform: node?.status === "pending" ? "translateY(0)" : "translateY(-3px)",
                transition: "opacity 0.3s ease, transform 0.3s ease",
                visibility: node?.status === "pending" ? "visible" : "hidden",
              }}
            >
              <CircularProgress size={14} sx={{ color: "#FFC107" }} />
              <Typography fontSize={11} sx={{ color: "#FFC107", fontWeight: "medium" }}>
                {data.statusMessage || "Queued for execution"}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
      {/* Output handles - show at least one output handle for most nodes */}
      {(() => {
        const outputs = data.model?.outputs || [];
        // Excel export nodes typically don't have output handles
        const isExcelExport = data.type === "data.exporttoexcel";
        const shouldShowOutputs =
          (outputs.length > 0 || data.type?.startsWith("trigger.") || data.model?.execute) &&
          !isExcelExport;
        const handlesToRender = outputs.length > 0 ? outputs : ["output"]; // Default to "output" handle

        return (
          shouldShowOutputs &&
          handlesToRender.map((output, index) => {
            const handleId = typeof output === "string" ? output : `output-${index}`;
            return data.name === "trigger" ? (
              <Handle
                key={handleId}
                type="source"
                position={Position.Right}
                id={handleId}
                style={{
                  backgroundColor: outputHandleColor,
                  width: 28,
                  height: 28,
                  right: "-15px",
                  borderRadius: "50%",
                  border: "2px solid white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 20,
                  fontWeight: "bold",
                  color: textColor,
                  cursor: "pointer",
                  top: `${50 + index * 20}%`,
                  transform: "translateY(-50%)",
                  zIndex: 10,
                }}
                title="Drag to connect"
              >
                +
              </Handle>
            ) : (
              <Handle
                key={handleId}
                type="source"
                position={Position.Right}
                id={handleId}
                style={{
                  backgroundColor: "#00C49F",
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  right: -9,
                  top: `${50 + index * 20}%`,
                  transform: "translateY(-50%)",
                  border: "2px solid white",
                }}
              />
            );
          })
        );
      })()}
    </Box>
  );
};

export default DefaultNode;
